import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import * as saveAs from 'file-saver';
import { ApiService } from '../../../../../@core/service/api.service';
import { SharedDataService } from '../../../../../@core/service/shared-data.service';
import { TreeService } from '../../../../../@core/service/tree.service';
import { CompilationModel, EPackingStatus, InvoiceUpload, packingStatusName } from '../../../../../@models/erp/compilation';
import { CompilationTypeData, DeliveryConditionsData, DocConfig, TaxHintData } from '../../../../../@models/erp/compilationData';
import { DocumentPrinterComponent, DownloadConfig } from '../document-printer.component';
import { EventService } from '../../../../../@core/service/eventService';
import { Changelog } from '../../../shared/changelog/changelog.component';
import { AuthGuardService } from '../../../../../@core/service/auth-guard.service';
import { EWarehousingEntryType, WarehousingModel } from '../../../../../@models/warehousing';
import { WarehousingComponent } from '../../../../warehousing/warehousing.component';
import { getObjectChanges, mongoId } from '../../../../../@core/common/global';
import { FileModel } from '../../../../../@models';
import { NrkWindowComponent } from '../../../shared/nrk-window/nrk-window.component';
import * as moment from 'moment';
import { ReportsPrinterComponent } from '../../../reports/reports-printer/reports-printer.component';
import { EDebugType } from '../../../../../@models/debug';
import { EMessagingWindowMode } from '../../../../warehousing/messaging/messaging.component';
import { DocumentSubComponentsComponent } from '../../document-sub-components/document-sub-components.component';
import { CommonDialogComponent, NrkDialogButton, NrkDialogType } from '../../../shared/common-dialog/common-dialog.component';

@Component({
  selector: 'ngx-popup-document',
  templateUrl: './popup-document.component.html',
  styleUrls: ['./popup-document.component.scss', '../document-printer.component.css'],
})
export class PopupDocumentComponent implements OnInit, OnChanges {

  @Input() compilation: CompilationModel;
  @Input() warehousingPopup = false;
  @Input() warehousingData = null;
  @Output() close = new EventEmitter<void>();
  @Output() reloadData = new EventEmitter<void>();

  @ViewChild('documentPrinter') documentPrinter: DocumentPrinterComponent;
  @ViewChild('reportsPrinter') reportsPrinter: ReportsPrinterComponent;
  // @ViewChild('messageWindow') messageWindow: NrkWindowComponent;
  @ViewChild('printUpdatedDnTpl') printUpdatedDnWindow: NrkWindowComponent;
  @ViewChild('packingComponent') packingComponent: DocumentSubComponentsComponent;

  taxHintOptions = TaxHintData.getOptions();
  deliveryConditionOptions = DeliveryConditionsData.getOptions();
  relatedDocuments = [];
  showDocumentPreview = true;

  currentMessage = new WarehousingModel();

  inputTableSaved = "opacity:0";

  docToPrint: CompilationModel;
  invoice: CompilationModel;
  pfInvoice: CompilationModel;

  page = 1;

  config = DocConfig;
  docType = CompilationTypeData;
  status = EPackingStatus;
  messagingWindowMode = EMessagingWindowMode;

  originalPacking: string;

  constructor(
    private api: ApiService, 
    private toastr: NbToastrService, 
    protected data: SharedDataService, 
    protected tree: TreeService, 
    protected event: EventService,
    private cdRef: ChangeDetectorRef,
    private auth: AuthGuardService,
    private dialogService: NbDialogService,
  ) {}

  ngOnInit(): void {
    if (this.warehousingPopup) {
      this.data.loadWarehousing();
      this.page = 2;
    } else {
      this.docToPrint = this.compilation;
    }
  }

  treeLoaded: boolean;
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.compilation?.currentValue) {
      this.treeLoaded = false;
      this.data.setDocConfigMaps(this.compilation.type);
      await this.tree.loadDocumentTree(this.compilation);
      if (this.packingComponent) this.packingComponent.disabled = true;
      this.treeLoaded = true;
      this.relatedDocuments = this.tree.getRelatedDocuments(this.compilation);
      this.invoice = this.relatedDocuments.map(m => m.doc)?.find(f => /^IN/.test(f.code))
      this.pfInvoice = this.relatedDocuments.map(m => m.doc)?.find(f => /^PFIN/.test(f.code))

      this.originalPacking = JSON.stringify(this.compilation.packingConditions);

      // console.log('ngOnChanges tree', this.invoice, this.pfInvoice, this.relatedDocuments);
    }
  }

  onClose() {
    console.log('get object changes', getObjectChanges(JSON.parse(this.originalPacking), this.compilation.packingConditions));
    if (this.originalPacking !== JSON.stringify(this.compilation.packingConditions)) {
      this.dialogService.open(CommonDialogComponent, {
        context: {
          dialogType: NrkDialogType.okCancel,
          message: `The packaging has changed.\nHave you reprinted the pallet label\nand delivery note?\n\nChanges:\n${getObjectChanges(JSON.parse(this.originalPacking), this.compilation.packingConditions)}`,
          okText: 'Yes',
          cancelText: 'No',
        }
      }).onClose.subscribe((x) => {
        if (x?.button === NrkDialogButton.save) {
          this.close.emit();
          if (this.packingComponent) this.packingComponent.disabled = true;
        }
      })
    } else {
      this.close.emit();
      if (this.packingComponent) this.packingComponent.disabled = true;
    }
  }

  getSelected(selection: string, options: {id: string, name: string}[]) {
    const sel = selection && options ? options.find(s => s.id.toString() === selection.toString()) : null;
    return sel ? sel.name : '';
  }

  onShowDocumentPreview(visible: boolean = true) {
    this.showDocumentPreview = visible;
  }

  onDownload(section: string) {
    this.api.downloadFile((this.compilation[section] as InvoiceUpload).files[0]);
  }

  async onDownloadPdf(merge: boolean = false, copy: boolean = false, zip: boolean = false, attachmentsOnly: boolean = false) {

    if (this.warehousingPopup && this.compilation.packingStatus > 0) {
      const doc = await this.api.loadCompilationByCode(this.compilation.code);
      // await Changelog.addAutolog(doc, this.auth.user, this.api, [{ 
      //   quickText: 'Updated Issue Date', 
      //   text: `from ${moment(doc.issueDate).format('YYYY-MM-DD')} to ${moment().format('YYYY-MM-DD')} (in Warehousing)`
      // }]); 
      const backup = JSON.parse(JSON.stringify(doc));
      doc.issueDate = this.compilation.issueDate = new Date();
      this.updateLog('issueDate', doc.issueDate, doc, backup);
      this.data.fiscalYearUpdate(doc);
      await this.api.saveCompilation(doc);
    }
    if (this.warehousingPopup) {
      if (zip || attachmentsOnly) {
        this.addWarehousingLog('Print DN', 'zip download')
      } else {
        this.addWarehousingLog('Print DN')
      }
    }
    
    this.documentPrinter?.onDownloadPdf(merge, copy, zip, attachmentsOnly);
  }

  async setPackingStatus(status: number) {
    // const doc = await this.api.loadCompilationByCode(this.compilation.code);

    // this.addWarehousingLog('Status Change', `from "${packingStatusName(doc.packingStatus)}" to "${packingStatusName(status)}"`);

    // doc.packingStatus = this.compilation.packingStatus = status;

    // this.api.saveCompilation(doc);

    this.addWarehousingLog('Status Change', `from "${packingStatusName(this.compilation.packingStatus)}" to "${packingStatusName(status)}"`);
    this.compilation.packingStatus = status;
    // this.tree.synchronizeSharedFields(this.compilation, ' (in warehousing)'))
    console.log('set packing status', this.compilation.packingStatus, status);
    await this.api.synchronizeField(this.compilation.code, 'packingStatus', status);
    const docref = await this.api.loadCompilationByCode(this.compilation.code);
    console.log('set packing status ref', docref.packingStatus, status);

  }

  async savePacking() {
    this.inputTableSaved = "color:green;transition:opacity 0.5s;opacity:1"
    this.cdRef.detectChanges()
    const doc = await this.api.loadCompilationByCode(this.compilation.code);
    doc.packingConditions = this.compilation.packingConditions;
    this.api.saveCompilation(doc).then(() => {
      // this.toastr.success('sucessfully updated', this.compilation.code);
      window.setTimeout(() => this.inputTableSaved = "color:green;transition:opacity 0.5s;opacity:0", 1000);
    });
  }

  async printTestCertificates() {
    // console.log('printTestCertificates')
    for (const tc of [...(this.compilation.testCertificates || []), ...this.linkedSpoTestCertificates]) {
      // console.log('printTestCertificates', tc.name, tc.url);
      await this.api.downloadFile(tc);
    }
    this.addWarehousingLog('Print Test Certificates');
  }

  get linkedSpoTestCertificates() {
    return this.tree.linkedPfspos?.flatMap(spo => spo.testCertificates) || [];
  }

  addWarehousingLog(subject: string, message: string = undefined) {
    PopupDocumentComponent.addWarehousingLog(this.compilation, this.data, this.api, this.auth, subject, message);
  }

  getLogEntriesFor(doc: CompilationModel) {
    return this.data.warehousingLog.filter(f => f.code === doc.code);
  }

  // async onImageUpload(files: FileModel[]) {
  //   const doc = await this.api.loadCompilationByCode(this.compilation.code);
  //   doc.packingImages = this.compilation.packingImages = files;
  //   // console.log('onImageUpload', files);
  //   this.api.saveCompilation(doc);
  //   this.addWarehousingLog('Image upload', files.map(m => `${m.name} [${m.url}]`)?.join(', '));
  //   this.api.debugLog('Warehousing image upload', files);
  // }

  async onFileUpload(files: FileModel[], field: string, logText: string, debugText: string = null) {
    const doc = await this.api.loadCompilationByCode(this.compilation.code);
    doc[field] = this.compilation[field] = files;
    // console.log('onImageUpload', files);
    this.api.saveCompilation(doc);
    this.addWarehousingLog(logText, files.map(m => `${m.name} [${m.url}]`)?.join(', '));
    this.api.debugLog(debugText || logText, files);
  }

  async onProveOfDeliveryUpload(upload: InvoiceUpload) {
    const doc = await this.api.loadCompilationByCode(this.compilation.code);
    upload.completed = true;
    doc.proveOfDelivery = this.compilation.proveOfDelivery = upload;
    // doc.packingStatus = this.compilation.packingStatus = EPackingStatus.delivered;
    // console.log('onProveOfDeliveryUpload', upload);
    this.api.saveCompilation(doc);
    this.addWarehousingLog('Image upload', upload.files.map(m => m.name)?.join(', '));
    this.api.debugLog('Warehousing image upload', upload.files);

    // sync to invoice
    const invoice = this.tree.findDocsByDownstreamType(this.compilation, 'IN');
    if (invoice && invoice[0]) {
      const doc = await this.api.loadCompilationByCode(invoice[0].code);
      doc.proveOfDelivery = upload;
      // console.log('onProveOfDeliveryUpload -> invoice', upload);
      this.api.saveCompilation(doc);
    }
  }


  static addWarehousingLog(doc: CompilationModel, data: SharedDataService, api: ApiService, auth: AuthGuardService, subject: string, message: string = undefined) {
    // console.log('add warehousing log', doc, doc.code);
    const entry = new WarehousingModel();
    entry.entryType = EWarehousingEntryType.documentLog;
    entry.code = doc.code;
    entry.user = auth.user;
    entry.timestamp = new Date();
    entry.subject = subject;
    entry.message = message;
    api.saveWarehousingEntry(entry).then(() => data.reloadWarehousing());
  }

  async reloadReportsData() {
    this.reportsPrinter.reloadData();
    this.compilation = await this.api.loadCompilationByCode(this.compilation.code);
  }

  async saveField(field: string) {
    const doc = await this.api.loadCompilationByCode(this.compilation.code);
    doc[field] = this.compilation[field];
    this.api.saveCompilation(doc);
  }

  async updateLog(field: string, date: Date, doc: CompilationModel, backup: CompilationModel) {

    let logSubject = '';
    let logEntry = ''
    
    if (field === 'vieDispatchDate') {
      logSubject = 'Updated Dispatch Date'
      logEntry = `from ${moment(backup.vieDispatchDate).format('YYYY-MM-DD')} to ${moment(date).format('YYYY-MM-DD')}`
    } else if (field === 'issueDate') {
      logSubject = 'Updated Issue Date';
      logEntry = `from ${moment(backup.issueDate).format('YYYY-MM-DD')} to ${moment(date).format('YYYY-MM-DD')}`
    } else if (field === 'dateOfDispatch') {
      logSubject = 'Updated Display Date (Dispatch)';
      logEntry = `from ${moment(backup.dateOfDispatch).format('YYYY-MM-DD')} to ${moment(date).format('YYYY-MM-DD')}`
    } else if (field === 'dateOfDelivery') {
      logSubject = 'Updated Display Date (ETA)';
      logEntry = `from ${moment(backup.dateOfDelivery).format('YYYY-MM-DD')} to ${moment(date).format('YYYY-MM-DD')}`
    }

    this.addWarehousingLog(logSubject, logEntry);
    await Changelog.addAndSaveAutolog(doc, this.auth.user, this.api, [{ 
      quickText: logSubject, 
      text: logEntry + ' (in Warehousing)',
    }]);

    // console.log('updateLog', doc.changelog);

  }


  async dispatchDateChanged(date: Date) {
    console.log('!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! dispatch date changed', date);
    // date in range +- 5 days
    const startRange = moment(this.compilation.vieDispatchDate).subtract(5, 'days');
    const endRange = moment(this.compilation.vieDispatchDate).add(5, 'days');
    const backup = JSON.parse(JSON.stringify(this.compilation));
    this.cdRef.detectChanges();

    if (true || (moment(date).isBetween(startRange, endRange) && moment().isBefore(date, 'day')) || moment(date).isBetween(moment().subtract(1, 'days'), moment().add(5, 'days'))) {

      let oc = this.compilation;
      do {
        oc = await this.api.loadCompilationByCode(oc?.parent);
      } while (oc && oc.type !== CompilationTypeData.get('OC').id);

      if (!oc) return;

      console.log('oc', oc.code, oc);
      // const oc = await this.api.loadCompilationByCode(this.tree.linkedOC.code);
      oc.vieDispatchDate = this.compilation.vieDispatchDate = date;
      oc.issueDate = this.compilation.issueDate = new Date();
      oc.dateOfDispatch = this.compilation.dateOfDispatch = date;
      oc.dateOfDelivery = this.compilation.dateOfDelivery = oc.etaCustomerDate;
      oc.editCount = this.compilation.editCount = oc.editCount + 1;

      this.updateLog('dateOfDispatch', oc.dateOfDispatch, oc, backup);
      this.updateLog('dateOfDelivery', oc.dateOfDelivery, oc, backup);
      this.updateLog('vieDispatchDate', date, oc, backup);
      this.updateLog('issueDate', new Date(), oc, backup);
      this.data.fiscalYearUpdate(oc);

      // console.log('dispatchDateChanged, changelog', doc.changelog, this.compilation.changelog, doc.code);
      await this.api.saveCompilation(oc);

      const doc = await this.api.loadCompilationByCode(this.compilation.code);
      doc.vieDispatchDate = this.compilation.vieDispatchDate = date;
      doc.issueDate = this.compilation.issueDate = new Date();
      doc.dateOfDispatch = this.compilation.dateOfDispatch = date;
      doc.dateOfDelivery = this.compilation.dateOfDelivery = doc.etaCustomerDate;
      doc.editCount = this.compilation.editCount = doc.editCount + 1;

      this.updateLog('dateOfDispatch', doc.dateOfDispatch, doc, backup);
      this.updateLog('dateOfDelivery', doc.dateOfDelivery, doc, backup);
      this.updateLog('vieDispatchDate', date, doc, backup);
      this.updateLog('issueDate', new Date(), doc, backup);
      this.data.fiscalYearUpdate(doc);

      // console.log('dispatchDateChanged, changelog', doc.changelog, this.compilation.changelog, doc.code);
      await this.api.saveCompilation(doc);

      this.printUpdatedDnWindow.show();

      this.tree.loadDocumentTree(this.compilation);
      this.reloadData.emit();

      // this.tree.synchronizeSharedFields(this.compilation).then(async () => {

      //   this.reloadData.emit();
      //   this.printUpdatedDnWindow.show();

      // });

    } else {
      this.compilation.vieDispatchDate = backup.vieDispatchDate;
      this.compilation.issueDate = backup.issueDate;
      this.data.fiscalYearUpdate(this.compilation);
      this.cdRef.detectChanges();
      this.toastr.danger(
        'New date has to be in range current date +/- 5 days\nand must not be in the past',
        moment(date).isBefore(moment(), 'day') ? 'Date is in the past' : 'Date out of range'
      );
    }

  }

  etaDispatchError(compilation: CompilationModel) {
    return compilation && moment(compilation.vieDispatchDate).isAfter(moment(compilation.etaCustomerDate), 'day');
  }

}
